import { Component, Prop, Vue } from "vue-property-decorator";
import WithRender from "./TimelineByCustomerTypeChart.html";
import Chart from "chart.js";
import { Inject } from "vue-di-container";
import RequestsOverviewService from "../RequestsOverviewService";
import {
    buildMonitorDashboardBaseTooltipStyling,
    buildMonitorDashboardCategoryAxisTicks,
    buildMonitorDashboardLegendTop,
    buildMonitorDashboardResponsiveChartOptions,
    buildMonitorDashboardValueAxisTicks,
    getMonitorDashboardFontFamily,
    getMonitorDashboardChartColor,
    monitorDashboardDarkerBorder,
    monitorDashboardLegendClickHandler,
    buildMonitorDashboardRequestsTimelineInnerStyle,
    buildMonitorDashboardRequestsTimelineLayoutPadding,
    buildMonitorDashboardRequestsTimelineScrollStyle,
    buildMonitorDashboardRequestsTimelineXAxisGridLines,
    scheduleMonitorDashboardChartResize,
} from "../../../chart/monitorDashboardChartKit";

const TIMELINE_CUSTOMER_TYPE_LABEL_MIN_SEGMENT_PX = 14;

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const CUSTOMER_TYPE_IDS = {
  Government: 1,
  Charity: 2,
  Private: 3,
};

type TimelineRow = {
  year: number;
  month: number;
  customerType: number;
  count: number;
};

type ChartTransformResult = {
  monthKeys: string[];
  labels: string[];
  valueMap: Record<string, number>;
};

@WithRender
@Component({
  diProvide: [RequestsOverviewService],
})
export default class TimelineByCustomerTypeChart extends Vue {
  @Inject(RequestsOverviewService)
  public requestsOverviewService!: RequestsOverviewService;

  @Prop({ required: true }) public filter!: any;

  private chart: any = null;
  private canvasId: string = "timeline-by-customer-type-chart-canvas";
  private barCount: number = 0;
  private scrollClientWidth: number = 0;
  private chartHeightPx: number = 0;

  public hasData: boolean = true;
  public isLoading: boolean = false;
  public totalCount: number = 0;
  private requestVersion: number = 0;

  private get customerTypes(): any[] {
    return [
      { id: CUSTOMER_TYPE_IDS.Government, label: String(this.$t("monitorDashboard.government")) },
      { id: CUSTOMER_TYPE_IDS.Private, label: String(this.$t("monitorDashboard.private")) },
      { id: CUSTOMER_TYPE_IDS.Charity, label: String(this.$t("monitorDashboard.charity")) },
    ];
  }

  public get scrollContainerStyle(): Record<string, string> {
    return buildMonitorDashboardRequestsTimelineScrollStyle(this.barCount);
  }

  public get innerStyle(): Record<string, string> {
    return buildMonitorDashboardRequestsTimelineInnerStyle(
      this.barCount,
      this.scrollClientWidth,
      this.chartHeightPx,
    );
  }

  public async refresh(): Promise<void> {
    if (!this.filter) return;

    this.isLoading = true;
    const currentRequestVersion = ++this.requestVersion;

    const payload = {
      dateFrom: this.filter.dateFrom,
      dateTo: this.filter.dateTo,
      tenantId: this.filter.tenant,
      providerId: this.filter.providerId,
      customerType: this.filter.customerType,
      requestType: this.filter.requestType,
    };

    const resp = await this.requestsOverviewService.GetRequestTimelineByCustomerType(payload);
    if (currentRequestVersion !== this.requestVersion) {
      return;
    }

    this.isLoading = false;

    if (!resp || !resp.result || !Array.isArray(resp.result)) {
      this.hasData = false;
      this.totalCount = 0;
      this.barCount = 0;
      this.destroyChart();
      return;
    }

    this.totalCount = resp.totalCount ? Number(resp.totalCount) : 0;

    await this.$nextTick();
    await this.renderChart(resp.result);
  }

  public onTitleClick(): void {
    this.navigateToTimelineByCustomerTypeDetails();
  }

  private navigateToTimelineByCustomerTypeDetails(): void {
    const f = this.filter;
    const overviewPayload = {
      dateFrom: f.dateFrom,
      dateTo: f.dateTo,
      tenantId: f.tenant,
      providerId: f.providerId,
      customerType: f.customerType || 0,
      requestType: f.requestType || 0,
    };

    try {
      sessionStorage.setItem(
        "monitorDashboard.overviewFilter",
        JSON.stringify(overviewPayload),
      );
    } catch (e) {
    }

    this.$router.push({ name: "TimelineByCustomerTypeDetails" });
  }

  private onChartClick(): void {
    this.navigateToTimelineByCustomerTypeDetails();
  }

  public beforeDestroy(): void {
    this.destroyChart();
  }

  private async syncTimelineDimensions(): Promise<void> {
    await this.$nextTick();
    await new Promise<void>(function (resolve) {
      window.requestAnimationFrame(function () {
        resolve();
      });
    });
    const scroll = this.$refs.timelineScroll as HTMLElement;
    const stage = this.$refs.timelineStage as HTMLElement;
    if (scroll && scroll.clientHeight > 0) {
      this.chartHeightPx = scroll.clientHeight;
    } else if (stage && stage.clientHeight > 0) {
      this.chartHeightPx = stage.clientHeight;
    } else {
      this.chartHeightPx = 0;
    }
    this.scrollClientWidth = scroll && scroll.clientWidth > 0 ? scroll.clientWidth : 0;
  }

  private async renderChart(rows: any[]): Promise<void> {
    const transformed = this.transformRows(rows);
    const monthKeys = transformed.monthKeys;
    const labels = transformed.labels;
    const valueMap = transformed.valueMap;

    this.barCount = labels.length;

    const datasets: any[] = [];
    let hasAnyNonZeroValue = false;

    for (let i = 0; i < this.customerTypes.length; i++) {
      const customerType = this.customerTypes[i];
      const data: number[] = [];

      for (let j = 0; j < monthKeys.length; j++) {
        const key = monthKeys[j] + ":" + String(customerType.id);
        const count = valueMap.hasOwnProperty(key) ? Number(valueMap[key]) : 0;
        data.push(count);
        if (count !== 0) hasAnyNonZeroValue = true;
      }

      const fill = getMonitorDashboardChartColor("timelineByCustomerType", String(customerType.id));
      datasets.push({
        label: customerType.label,
        data: data,
        backgroundColor: fill,
        borderColor: monitorDashboardDarkerBorder(fill),
        borderWidth: 2,
        categoryPercentage: 0.7,
        barPercentage: 0.85,
      });
    }

    this.hasData = hasAnyNonZeroValue;
    this.destroyChart();
    if (!hasAnyNonZeroValue) {
      return;
    }

    await this.syncTimelineDimensions();
    await this.$nextTick();

    const yAxisMax = this.getYAxisMaxStacked(datasets);

    const el = document.getElementById(this.canvasId) as HTMLCanvasElement;
    if (!el) return;

    const ctx = el.getContext("2d") as any;
    const self = this;
    const fontFamily = getMonitorDashboardFontFamily();
    const baseOpts = buildMonitorDashboardResponsiveChartOptions();

    this.chart = new Chart(ctx, {
      type: "bar",
      data: {
        labels: labels,
        datasets: datasets,
      },
      plugins: [
        {
          afterDatasetsDraw: (chart: any) => {
            this.drawZeroValueMarkers(chart);
            this.drawValueLabels(chart);
          },
        },
      ],
      options: Object.assign({}, baseOpts, {
        onClick: function () {
          self.onChartClick();
        },
        layout: {
          padding: buildMonitorDashboardRequestsTimelineLayoutPadding(),
        },
        legend: buildMonitorDashboardLegendTop(fontFamily, monitorDashboardLegendClickHandler),
        scales: {
          xAxes: [
            {
              stacked: true,
              gridLines: buildMonitorDashboardRequestsTimelineXAxisGridLines(),
              ticks: buildMonitorDashboardCategoryAxisTicks(14, fontFamily),
            },
          ],
          yAxes: [
            {
              stacked: true,
              ticks: buildMonitorDashboardValueAxisTicks(12, fontFamily, true, yAxisMax, this.getYAxisStep(yAxisMax)),
            },
          ],
        },
        tooltips: Object.assign({}, buildMonitorDashboardBaseTooltipStyling(), {
          mode: "index",
          intersect: false,
          callbacks: {
            title: function (tooltipItems: any[]) {
              if (!tooltipItems || tooltipItems.length === 0) return "";
              const idx = tooltipItems[0].index;
              return labels[idx];
            },
            label: function (tooltipItem: any, data: any) {
              const dsIndex = tooltipItem.datasetIndex;
              const idx = tooltipItem.index;
              const ds = data && data.datasets && data.datasets[dsIndex] ? data.datasets[dsIndex] : null;
              const raw = ds && ds.data && ds.data.length > idx ? Number(ds.data[idx]) : 0;
              const value = isNaN(raw) ? 0 : raw;
              return String(ds && ds.label ? ds.label : "") + ": " + String(value);
            },
            afterBody: function (items: any[], data: any) {
              let monthTotal = 0;
              if (items && items.length) {
                for (let i = 0; i < items.length; i++) {
                  const item = items[i];
                  const ds = data && data.datasets && data.datasets[item.datasetIndex]
                    ? data.datasets[item.datasetIndex]
                    : null;
                  const raw = ds && ds.data && ds.data.length > item.index ? Number(ds.data[item.index]) : 0;
                  const value = isNaN(raw) ? 0 : raw;
                  monthTotal += value;
                }
              }
              return String(self.$t("monitorDashboard.total")) + ": " + String(monthTotal);
            },
          },
        }),
      }),
    });

    scheduleMonitorDashboardChartResize(this.chart, this);
  }

  private transformRows(rows: any[]): ChartTransformResult {
    const monthKeys: string[] = [];
    const monthKeySet: Record<string, boolean> = {};
    const valueMap: Record<string, number> = {};

    for (let i = 0; i < rows.length; i++) {
      const normalized = this.normalizeTimelineRow(rows[i]);
      const monthKey = this.getMonthKey(normalized.year, normalized.month);

      if (!monthKeySet[monthKey]) {
        monthKeySet[monthKey] = true;
        monthKeys.push(monthKey);
      }

      valueMap[this.getValueMapKey(monthKey, normalized.customerType)] = normalized.count;
    }

    monthKeys.sort((a: string, b: string) => {
      const monthA = this.parseMonthKey(a);
      const monthB = this.parseMonthKey(b);
      if (monthA.year !== monthB.year) return monthB.year - monthA.year;
      return monthB.month - monthA.month;
    });

    const labels: string[] = [];
    for (let i = 0; i < monthKeys.length; i++) {
      const month = this.parseMonthKey(monthKeys[i]);
      labels.push(this.formatMonthLabel(month.year, month.month));
    }

    return {
      monthKeys: monthKeys,
      labels: labels,
      valueMap: valueMap,
    };
  }

  private normalizeTimelineRow(row: any): TimelineRow {
    return {
      year: Number(row.year),
      month: Number(row.month),
      customerType: Number(row.customerType),
      count: Number(row.count),
    };
  }

  private getMonthKey(year: number, month: number): string {
    return String(year) + "-" + String(month);
  }

  private parseMonthKey(key: string): { year: number; month: number } {
    const parts = key.split("-");
    return {
      year: Number(parts[0]),
      month: Number(parts[1]),
    };
  }

  private getValueMapKey(monthKey: string, customerType: number): string {
    return monthKey + ":" + String(customerType);
  }

  private drawValueLabels(chart: any): void {
    const ctx2: any = chart.ctx;
    const yAxis = chart.scales["y-axis-0"];
    if (!yAxis) return;
    const yZero = yAxis.getPixelForValue(0);

    ctx2.save();
    ctx2.textAlign = "center";
    ctx2.textBaseline = "bottom";
    ctx2.font = "10px " + getMonitorDashboardFontFamily();
    ctx2.fillStyle = "#6b7280";

    const monthHasNonZero = this.getMonthHasNonZeroValues(chart);

    for (let di = 0; di < chart.data.datasets.length; di++) {
      const meta = chart.getDatasetMeta(di);
      if (!meta || meta.hidden) continue;

      for (let i = 0; i < meta.data.length; i++) {
        const val = Number(chart.data.datasets[di].data[i]);
        const bar = meta.data[i];
        if (!bar || !bar._model) continue;

        ctx2.textBaseline = "bottom";
        ctx2.font = "10px " + getMonitorDashboardFontFamily();

        if (val === 0) {
          if (monthHasNonZero[i]) {
            continue;
          }
          ctx2.fillStyle = "#444444";
          ctx2.fillText("0", bar._model.x, yZero - 6);
          continue;
        }

        const x = bar._model.x;
        const yEnd = Number(bar._model.y);
        const yBase = Number(bar._model.base);
        const segmentTop = yEnd < yBase ? yEnd : yBase;
        const segmentBottom = yEnd < yBase ? yBase : yEnd;
        const segmentHeight = segmentBottom - segmentTop;

        if (segmentHeight < TIMELINE_CUSTOMER_TYPE_LABEL_MIN_SEGMENT_PX) {
          continue;
        }

        const labelY = (segmentTop + segmentBottom) / 2;
        ctx2.textBaseline = "middle";
        ctx2.font = segmentHeight >= 18 ? "11px " + getMonitorDashboardFontFamily() : "10px " + getMonitorDashboardFontFamily();
        ctx2.fillStyle = "#FFFFFF";
        ctx2.fillText(String(val), x, labelY);
      }
    }

    ctx2.restore();
  }

  private drawZeroValueMarkers(chart: any): void {
    const ctx2: any = chart.ctx;
    const yAxis = chart.scales["y-axis-0"];
    if (!yAxis) return;

    const yZero = yAxis.getPixelForValue(0);

    ctx2.save();
    ctx2.lineWidth = 3;

    const monthHasNonZero = this.getMonthHasNonZeroValues(chart);

    for (let di = 0; di < chart.data.datasets.length; di++) {
      const meta = chart.getDatasetMeta(di);
      if (!meta || meta.hidden) continue;

      const strokeColor = chart.data.datasets[di].borderColor;
      ctx2.strokeStyle = strokeColor;

      for (let i = 0; i < meta.data.length; i++) {
        const val = Number(chart.data.datasets[di].data[i]);
        if (val !== 0) continue;
        if (monthHasNonZero[i]) continue;

        const bar = meta.data[i];
        if (!bar || !bar._model) continue;

        const x = bar._model.x;
        const halfWidth = Math.max(1, (bar._model.width || 0) / 2);
        const left = x - halfWidth + 2;
        const right = x + halfWidth - 2;

        ctx2.beginPath();
        ctx2.moveTo(left, yZero);
        ctx2.lineTo(right, yZero);
        ctx2.stroke();
      }
    }

    ctx2.restore();
  }

  private getMonthHasNonZeroValues(chart: any): boolean[] {
    const datasets = chart && chart.data && chart.data.datasets ? chart.data.datasets : [];
    const pointsCount = datasets[0] && datasets[0].data ? datasets[0].data.length : 0;
    const monthHasNonZero: boolean[] = [];

    for (let i = 0; i < pointsCount; i++) {
      let hasNonZero = false;
      for (let j = 0; j < datasets.length; j++) {
        if (Number(datasets[j].data[i] || 0) !== 0) {
          hasNonZero = true;
          break;
        }
      }
      monthHasNonZero.push(hasNonZero);
    }

    return monthHasNonZero;
  }

  private formatMonthLabel(year: number, month: number): string {
    const monthIndex = month >= 1 && month <= 12 ? month - 1 : 0;
    const shortYear = year >= 2000 ? String(year).slice(-2) : String(year);
    return MONTH_NAMES[monthIndex] + " " + shortYear;
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  private getYAxisStep(maxVal: number): number {
    const targetTicks = 22;
    const rawStep = Math.ceil(maxVal / targetTicks);
    return this.getNiceStep(rawStep);
  }

  private getNiceStep(rawStep: number): number {
    if (rawStep <= 1) return 1;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const normalized = rawStep / magnitude;
    if (normalized <= 1) return 1 * magnitude;
    if (normalized <= 2) return 2 * magnitude;
    if (normalized <= 5) return 5 * magnitude;
    return 10 * magnitude;
  }

  private getYAxisMaxStacked(datasets: any[]): number {
    if (!datasets || datasets.length === 0) return 10;

    const pointsCount = datasets[0] && datasets[0].data ? datasets[0].data.length : 0;
    let maxVal = 0;

    for (let i = 0; i < pointsCount; i++) {
      let stackedTotal = 0;
      for (let j = 0; j < datasets.length; j++) {
        stackedTotal += Number(datasets[j].data[i] || 0);
      }
      if (stackedTotal > maxVal) {
        maxVal = stackedTotal;
      }
    }

    if (maxVal <= 0) return 10;
    const withPadding = Math.ceil(maxVal * 1.01);
    const step = this.getYAxisStep(withPadding);
    return Math.ceil(withPadding / step) * step;
  }
}
